import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { GridPaginationModel } from '@mui/x-data-grid-pro'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { ExecuteRefundModal } from 'src/components/payments-ops/refunds/ExecuteRefundModal'
import { RefundTabs } from 'src/components/payments-ops/refunds/RefundTabs'
import { RefundsTable } from 'src/components/payments-ops/refunds/RefundsTable'
import { AdminFetchRefunds } from 'src/constants/actionTypes'
import { PAYMENTS_OPS_DEFAULT_PAGE_SIZE } from 'src/constants/payments-ops/constants'
import { RefundStatus } from 'src/constants/payments-ops/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import type { FetchRefundsPayload, Refund } from 'src/interfaces/refund'
import { getRefundsError } from 'src/selectors/refund'
import { useKillswitchStates } from 'src/utils/hooks/paymentsOps/useKillswitchStates'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { useCanRelyOnKillswitchStatus } from 'src/utils/hooks/paymentsOps/useCanRelyOnKillswitchStatus'
import { useQueryParamUpdater } from 'src/utils/hooks/paymentsOps/useQueryParamUpdater'

const KILLSWITCH_EXECUTE_REASON = 'Payment processing is stopped — lift the killswitch first.'
const PARTNER_KILLSWITCH_EXECUTE_REASON =
  'Payment processing is stopped for this partner — lift the killswitch first.'
const KILLSWITCH_UNKNOWN_EXECUTE_REASON = 'Killswitch status unavailable — reload the page'

const RefundsPageComponent = () => {
  const [searchParams] = useSearchParams()
  const updateParams = useQueryParamUpdater()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const { payments } = useKillswitchStates()
  // Without `feature_flags_view` the flags are never fetched; Execute is then not held back on an
  // unknown status — para-node's RefundService refuses execution while payment processing is
  // stopped (spec §2.2, §10.3).
  const isKillswitchStatusKnown = useCanRelyOnKillswitchStatus()
  const canExecute = useHasPermission(InternalPermission.RefundsExecute)
  const listError = useSelector(getRefundsError)

  const [executingRefund, setExecutingRefund] = useState<Refund | null>(null)

  const tabParam = searchParams.get('tab')
  const status =
    Object.values(RefundStatus).find((candidate) => {
      return candidate === tabParam
    }) ?? RefundStatus.Created

  const paginationModel = useMemo(() => {
    const pageSize = Number(searchParams.get('limit')) || PAYMENTS_OPS_DEFAULT_PAGE_SIZE
    const offset = Number(searchParams.get('offset')) || 0

    return { page: Math.floor(offset / pageSize), pageSize }
  }, [searchParams])

  const request = useMemo<FetchRefundsPayload>(() => {
    return {
      status,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }
  }, [paginationModel, status])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchRefunds.request(request))
  }, [asyncDispatchWithErrorToast, isAuthenticated, request])

  const handleTabChange = useCallback(
    (nextStatus: RefundStatus) => {
      // TableTab also fires for the selected tab; that must not reset the page offset.
      if (nextStatus === status) {
        return
      }

      updateParams({ tab: nextStatus, offset: null })
    },
    [status, updateParams],
  )

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      updateParams({ limit: String(model.pageSize), offset: String(model.page * model.pageSize) })
    },
    [updateParams],
  )

  // Per row: the global switch, a per-partner row and an unknown status each block Execute.
  const getExecuteDisabledReason = useCallback(
    (refund: Refund): string | null => {
      if (!isKillswitchStatusKnown) {
        return KILLSWITCH_UNKNOWN_EXECUTE_REASON
      }

      if (payments.isGloballyActive) {
        return KILLSWITCH_EXECUTE_REASON
      }

      if (payments.activePartnerIds.includes(refund.partnerId)) {
        return PARTNER_KILLSWITCH_EXECUTE_REASON
      }

      return null
    },
    [isKillswitchStatusKnown, payments.activePartnerIds, payments.isGloballyActive],
  )

  const handleCloseExecute = useCallback(() => {
    setExecutingRefund(null)
  }, [])

  return (
    <div
      data-testid="refunds-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <PageHeader title="Refunds" description="Execute created or failed refunds." />
      <div css={tw`shrink-0`}>
        <RefundTabs selectedStatus={status} onChange={handleTabChange} />
      </div>
      {listError ? (
        <Typography variant="body2" css={tw`bg-shades-0 px-4 py-3 text-critical-500`}>
          {listError}
        </Typography>
      ) : null}
      <RefundsTable
        status={status}
        paginationModel={paginationModel}
        canExecute={canExecute}
        getExecuteDisabledReason={getExecuteDisabledReason}
        onPaginationModelChange={handlePaginationModelChange}
        onExecute={setExecutingRefund}
      />
      <ExecuteRefundModal refund={executingRefund} refetch={request} onClose={handleCloseExecute} />
    </div>
  )
}

export const RefundsPage = memo(RefundsPageComponent)
